import { usePageTitle } from '@/hooks/usePageTitle';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Archive, ArchiveRestore, ArrowUpDown, Building2, CheckSquare, Columns3, Copy, Eye, FileText, Ghost, LayoutGrid, List, LogOut, MoreHorizontal, Package, Pencil, Plug, Plus, RotateCcw, Search, Star, Trash2, User, X } from 'lucide-react';
import { toast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useQuoteStore } from '@/store/quoteStore';
import { useTemplateStore } from '@/store/templateStore';
import { BlockType, QuoteStatus } from '@/types';
import type { DealOption, Template, Quote } from '@/types';
import {
  connectHubSpot,
  getHubSpotContext,
  hasHubSpotAuthToken,
} from '@/lib/hubspotContext';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { logoutHubSpotSession } from '@/lib/hubspotSession';
import { getQuoteClientLogo } from '@/store/quoteClientLogoContext';
import { isTemplateEmpty } from '@/lib/templateContent';
import TemplatePreviewDialog from '@/components/TemplatePreviewDialog';
import PageLibraryTab from '@/components/library/PageLibraryTab';
import { usePageLibraryStore } from '@/store/pageLibraryStore';
import QuotePipelineBoard from '@/components/quotes/QuotePipelineBoard';
import PageContainer from '@/components/layout/PageContainer';
import QuoteCard from '@/components/quotes/QuoteCard';
import DealPicker from '@/components/quotes/DealPicker';
import QuoteMoreFilters from '@/components/quotes/QuoteMoreFilters';
import QuoteSignalFilters from '@/components/quotes/QuoteSignalFilters';
import QuoteTable from '@/components/quotes/QuoteTable';
import QuoteTotalsFooter from '@/components/quotes/QuoteTotalsFooter';
import { quotesMaxValueCents } from '@/components/quotes/pipelineFormat';
import type { QuoteActionHandlers } from '@/components/quotes/QuoteRowActions';
import {
  DEFAULT_ADVANCED_FILTERS,
  clearAdvancedFilter,
  describeAdvancedFilters,
  matchesAdvancedFilters,
  type QuoteAdvancedFilters,
} from '@/components/quotes/quoteFilters';
import { getQuoteOwnerLabel, getQuoteOwnerSearchText } from '@/components/quotes/quoteMeta';
import { SIGNAL_LABELS, countSignals, matchesSignal, type QuoteSignal } from '@/components/quotes/quoteSignals';

type QuoteStatusTab = 'alles' | 'concept' | 'openstaand' | 'geaccepteerd' | 'afgewezen' | 'spook' | 'gearchiveerd';
type QuoteViewMode = 'grid' | 'list';
type QuoteScope = 'deal' | 'portal';

const STATUS_TAB_FILTERS: Record<Exclude<QuoteStatusTab, 'spook' | 'alles' | 'gearchiveerd'>, string[]> = {
  concept: ['DRAFT', 'WITHDRAWN'],
  openstaand: ['SENT', 'VIEWED'],
  geaccepteerd: ['ACCEPTED'],
  afgewezen: ['DECLINED', 'EXPIRED'],
};

const GHOST_TAB_HINT =
  'Concepten die minstens 24 uur geleden zijn aangemaakt en daarna nooit zijn geopend of bewerkt.';

const ARCHIVE_TAB_HINT = 'Gearchiveerde offertes staan hier en zet je met één klik terug.';

/**
 * Spookoffertes filteren op de afgeleide vlag. Onder Concept staan ze standaard uit
 * (`hideGhosts`), zodat het conceptoverzicht alleen offertes toont waar echt aan gewerkt is.
 * Gearchiveerde offertes staan alleen onder Gearchiveerd.
 */
function matchesStatusTab(quote: Quote, tab: QuoteStatusTab, hideGhosts: boolean) {
  if (tab === 'gearchiveerd') return Boolean(quote.archivedAt);
  if (quote.archivedAt) return false;
  if (tab === 'spook') return quote.isGhost === true;
  if (tab === 'alles') return !(hideGhosts && quote.isGhost);
  if (!STATUS_TAB_FILTERS[tab].includes(quote.status)) return false;
  if (tab === 'concept' && hideGhosts && quote.isGhost) return false;
  return true;
}

const STATUS_TAB_LABELS: Record<QuoteStatusTab, string> = {
  alles: 'Alles',
  concept: 'Concept',
  openstaand: 'Openstaand',
  geaccepteerd: 'Geaccepteerd',
  afgewezen: 'Afgewezen',
  spook: 'Spookoffertes',
  gearchiveerd: 'Gearchiveerd',
};

type SortOption = 'updated_desc' | 'created_desc' | 'created_asc' | 'name_asc' | 'name_desc';

const SORT_LABELS: Record<SortOption, string> = {
  updated_desc: 'Laatst gewijzigd',
  created_desc: 'Nieuwste eerst',
  created_asc: 'Oudste eerst',
  name_asc: 'Naam A-Z',
  name_desc: 'Naam Z-A',
};

/**
 * Templates staan op aanmaakdatum: een template opslaan in de editor mag hem niet
 * bovenaan zetten, anders schuift de volgorde bij elke aanpassing.
 */
type TemplateSortOption = Exclude<SortOption, 'updated_desc'>;
const TEMPLATE_SORT_OPTIONS: TemplateSortOption[] = ['created_desc', 'created_asc', 'name_asc', 'name_desc'];

function isWithinDays(date: Date, days: number) {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  return diff >= 0 && diff <= days * 86400000;
}

function escapePopupHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function SkeletonCards() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-lg border bg-card p-6 animate-pulse">
          <div className="h-5 w-3/4 bg-muted rounded mb-3" />
          <div className="h-4 w-1/4 bg-muted rounded mb-2" />
          <div className="h-3 w-1/2 bg-muted rounded mb-4" />
          <div className="h-8 w-20 bg-muted rounded" />
        </div>
      ))}
    </div>
  );
}

function HomePage() {
  usePageTitle('Offertes');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { quotes, quotesLoading, fetchQuotes, setQuotesArchived } = useQuoteStore();
  const { templates, loading: templatesLoading, fetchTemplates } = useTemplateStore();
  const context = useMemo(() => getHubSpotContext(), []);
  const isAuthenticated = hasHubSpotAuthToken();
  const missingPortalId = !context.portalId;
  const needsHubSpotSetup = missingPortalId || !isAuthenticated;

  const activeTab = searchParams.get('tab') || 'quotes';
  const setActiveTab = useCallback((tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'quotes') next.delete('tab');
      else next.set('tab', tab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [searchQuery, setSearchQuery] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  /** HubSpot deal-eigenaar (owner id) */
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState<SortOption>('updated_desc');
  const [templateSortBy, setTemplateSortBy] = useState<TemplateSortOption>('created_desc');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [viewMode, setViewMode] = useState<QuoteViewMode>(() => {
    const stored = localStorage.getItem('quote_view_mode');
    return stored === 'grid' || stored === 'list' ? stored : 'grid';
  });

  const [quoteStatusTab, setQuoteStatusTab] = useState<QuoteStatusTab>('concept');
  /** Secundaire filters (o.a. spookoffertes verbergen) zitten achter 'Meer filters'. */
  const [advancedFilters, setAdvancedFilters] = useState<QuoteAdvancedFilters>(DEFAULT_ADVANCED_FILTERS);
  /**
   * Eén actief signaal uit 'Vraagt actie'. Een signaal loopt dwars door de
   * statussen, dus zolang het aanstaat vervangt het de statustab in plaats van
   * er bovenop te filteren — anders kijk je naar een lege lijst zonder te zien
   * waarom.
   */
  const [signalFilter, setSignalFilter] = useState<QuoteSignal | null>(null);
  const hideGhostQuotes = advancedFilters.hideGhosts;
  const [storedQuoteScope, setQuoteScope] = useState<QuoteScope>(() => {
    const stored = localStorage.getItem('quote_scope');
    return stored === 'portal' || stored === 'deal' ? stored : 'deal';
  });
  const quoteScope: QuoteScope = context.dealId ? storedQuoteScope : 'portal';
  /**
   * Het bereik doet ertoe in de HubSpot-dealkaart (iframe). In een losse tab komt
   * de dealId hooguit uit localStorage van een eerder bezoek; staat de lijst daar
   * op 'deze deal', dan blijft de switch staan zodat je naar het overzicht kunt.
   */
  const showQuoteScopeSwitch =
    (window.self !== window.top && Boolean(context.dealId)) || quoteScope === 'deal';
  const [selectedQuoteIds, setSelectedQuoteIds] = useState<Set<string>>(new Set());
  const [bulkArchiveLoading, setBulkArchiveLoading] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [connectingHubSpot, setConnectingHubSpot] = useState(false);

  const toggleQuoteSelection = useCallback((id: string) => {
    setSelectedQuoteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedQuoteIds(new Set());
    setSelectionMode(false);
  }, []);

  const fetchVisibleQuotes = useCallback(async () => {
    if (!context.portalId) return;
    // Gearchiveerde offertes laden mee, zodat de tab Gearchiveerd direct opent.
    if (quoteScope === 'portal' || !context.dealId) {
      await fetchQuotes({ portalId: context.portalId, limit: 1000, includeArchived: true });
      return;
    }
    await fetchQuotes({ dealId: context.dealId, portalId: context.portalId, includeArchived: true });
  }, [context.dealId, context.portalId, fetchQuotes, quoteScope]);

  const handleConnectHubSpot = useCallback(async () => {
    setConnectingHubSpot(true);
    const result = await connectHubSpot();
    setConnectingHubSpot(false);

    switch (result.status) {
      case 'connected':
        // Herladen zodat portalId, context en offertelijst allemaal van de nieuwe sessie uitgaan.
        window.location.reload();
        return;
      case 'popup_blocked':
        toast.error('Sta pop-ups toe voor deze pagina om HubSpot te verbinden.');
        return;
      case 'error':
        toast.error(result.message || 'Verbinden met HubSpot mislukt');
        return;
      case 'cancelled':
        return;
      default: {
        const exhaustive: never = result;
        return exhaustive;
      }
    }
  }, []);

  const handleQuoteScopeChange = useCallback((scope: QuoteScope) => {
    setQuoteScope(scope);
    localStorage.setItem('quote_scope', scope);
    clearSelection();
    setCompanyFilter('all');
    setOwnerFilter('all');
    setDateFilter('all');
    if (scope === 'portal') setQuoteStatusTab('openstaand');
  }, [clearSelection]);

  /**
   * Offertes worden gearchiveerd, niet verwijderd. Omdat dat terug te draaien is
   * volgt er geen bevestigingsvraag maar een toast met 'Ongedaan maken'.
   */
  const changeArchived = useCallback(
    async (ids: string[], archived: boolean) => {
      try {
        await setQuotesArchived(ids, archived);
      } catch (error) {
        toast.error(archived ? 'Archiveren mislukt' : 'Terugzetten mislukt', { error });
        return false;
      }
      const subject = ids.length === 1 ? 'Offerte' : `${ids.length} offertes`;
      const hadClientLink = quotes.some(
        (q) => ids.includes(q.id) && q.status !== QuoteStatus.DRAFT && q.status !== QuoteStatus.WITHDRAWN,
      );
      toast.success(archived ? `${subject} gearchiveerd` : `${subject} teruggezet`, {
        description:
          archived && hadClientLink ? 'De klantlink werkt niet meer tot je de offerte terugzet.' : undefined,
        action: {
          label: 'Ongedaan maken',
          onClick: () => {
            setQuotesArchived(ids, !archived).catch((error) =>
              toast.error('Ongedaan maken mislukt', { error })
            );
          },
        },
      });
      return true;
    },
    [quotes, setQuotesArchived],
  );

  const handleBulkArchive = async (archived: boolean) => {
    if (selectedQuoteIds.size === 0) return;
    setBulkArchiveLoading(true);
    const done = await changeArchived(Array.from(selectedQuoteIds), archived);
    setBulkArchiveLoading(false);
    if (done) clearSelection();
  };

  const [extendOpen, setExtendOpen] = useState(false);
  const [extendQuoteId, setExtendQuoteId] = useState('');
  const [extendDate, setExtendDate] = useState('');
  const [extendLoading, setExtendLoading] = useState(false);
  const [withdrawTarget, setWithdrawTarget] = useState<Quote | null>(null);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  /** `${quoteId}:countersign` | `${quoteId}:signed` tijdens API-call */
  const [emailResendBusy, setEmailResendBusy] = useState<string | null>(null);
  const [countersignLinkBusy, setCountersignLinkBusy] = useState<string | null>(null);
  const [pdfDownloadBusy, setPdfDownloadBusy] = useState<string | null>(null);

  const handleViewModeChange = useCallback((mode: QuoteViewMode) => {
    setViewMode(mode);
    localStorage.setItem('quote_view_mode', mode);
  }, []);

  const handleExtendQuote = async () => {
    if (!extendQuoteId || !extendDate) return;
    setExtendLoading(true);
    try {
      const { updateQuote: storeUpdate } = useQuoteStore.getState();
      await storeUpdate(extendQuoteId, { status: QuoteStatus.SENT, expiresAt: new Date(extendDate) } as any);
      await fetchVisibleQuotes();
      toast.success('Offerte verlengd!');
      setExtendOpen(false);
    } catch (error) {
      toast.error('Verlengen mislukt', { error });
    } finally {
      setExtendLoading(false);
    }
  };

  const openExtendDialog = useCallback((quoteId: string) => {
    const thirtyDays = new Date();
    thirtyDays.setDate(thirtyDays.getDate() + 30);
    setExtendQuoteId(quoteId);
    setExtendDate(thirtyDays.toISOString().split('T')[0]);
    setExtendOpen(true);
  }, []);

  const handleWithdrawQuote = async () => {
    if (!withdrawTarget) return;
    setWithdrawLoading(true);
    try {
      await api.withdrawQuote(withdrawTarget.id);
      await fetchVisibleQuotes();
      if (selectedQuoteIds.has(withdrawTarget.id)) {
        setSelectedQuoteIds((prev) => {
          const next = new Set(prev);
          next.delete(withdrawTarget.id);
          return next;
        });
      }
      toast.success('Offerte ingetrokken. De klantlink is niet meer beschikbaar.');
      setWithdrawTarget(null);
    } catch (err: unknown) {
      toast.error('Offerte intrekken mislukt', { error: err });
    } finally {
      setWithdrawLoading(false);
    }
  };

  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  /** Deal waar de nieuwe offerte aan komt te hangen; vooraf ingevuld vanaf een dealkaart. */
  const [pickerDeal, setPickerDeal] = useState<DealOption | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<Template | null>(null);
  const [autoCreating, setAutoCreating] = useState(false);

  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [saveAsTemplateQuoteId, setSaveAsTemplateQuoteId] = useState('');
  const [saveAsTemplateName, setSaveAsTemplateName] = useState('');
  const [saveAsTemplateDesc, setSaveAsTemplateDesc] = useState('');
  const [saveAsTemplateLoading, setSaveAsTemplateLoading] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  /** Alleen templates: offertes worden gearchiveerd in plaats van verwijderd. */
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string; hasDraft: boolean } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [newTemplateOpen, setNewTemplateOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDesc, setNewTemplateDesc] = useState('');
  /** '__blank__' voor een lege template, anders het id van de template die je kopieert. */
  const [newTemplateSource, setNewTemplateSource] = useState('__blank__');
  const [newTemplateLoading, setNewTemplateLoading] = useState(false);

  const [newLibraryPageOpen, setNewLibraryPageOpen] = useState(false);
  const libraryPageCount = usePageLibraryStore((state) => state.pages.filter((page) => !page.archivedAt).length);
  const ensureLibraryPages = usePageLibraryStore((state) => state.ensurePages);
  useEffect(() => {
    if (context.portalId) ensureLibraryPages();
  }, [context.portalId, ensureLibraryPages]);

  useEffect(() => {
    void fetchVisibleQuotes();
  }, [fetchVisibleQuotes]);

  useEffect(() => {
    if (context.portalId) {
      fetchTemplates({ portalId: context.portalId, includePublic: false, includeArchived });
    }
  }, [context.portalId, fetchTemplates, includeArchived]);

  const [openPickerOnLoad, setOpenPickerOnLoad] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('createNew') !== 'true' || !context.portalId || !context.dealId || autoCreating) return;

    const templateId = params.get('templateId') || undefined;
    if (templateId) {
      setAutoCreating(true);
      api.createQuoteFromTemplate({
        portalId: context.portalId,
        dealId: context.dealId,
        templateId,
        title: 'Nieuwe Offerte',
      }).then((quote) => {
        navigate(`/quotes/${quote.id}/edit`);
      }).catch((err) => {
        console.error('Auto-create quote failed:', err);
        setAutoCreating(false);
      });
    } else {
      setOpenPickerOnLoad(true);
    }
  }, [context.portalId, context.dealId, autoCreating, navigate]);

  const refreshTemplates = useCallback(async () => {
    if (context.portalId) {
      await fetchTemplates({ portalId: context.portalId, includePublic: false, includeArchived });
    }
  }, [context.portalId, fetchTemplates, includeArchived]);

  /** Templates waarmee je een offerte kunt beginnen: niet gearchiveerd en niet leeg. */
  const startableTemplates = useMemo(
    () => templates.filter((t) => !t.archivedAt && !isTemplateEmpty(t)),
    [templates],
  );

  /**
   * Open 'Nieuwe offerte'. Zonder deal in de context (tool los geopend in plaats
   * van vanaf een dealkaart) kies je de deal in de dialoog zelf.
   */
  const handleOpenPicker = (templateId?: string) => {
    if (!context.portalId) {
      toast.error('Portal ID ontbreekt.');
      return;
    }
    const master = startableTemplates.find((t) => t.isMaster);
    setSelectedTemplateId(templateId || master?.id || '__blank__');
    const dealId = context.dealId;
    if (dealId) {
      const known = quotes.find((q) => q.dealId === dealId);
      setPickerDeal({ id: dealId, name: known?.dealName || `Deal ${dealId}`, ownerName: known?.dealOwnerName });
      // Bedrijf, fase en bedrag komen uit HubSpot; tot die er zijn staat de deal er al met naam.
      api
        .getDealOption(dealId)
        .then((deal) => setPickerDeal((prev) => (prev?.id === dealId ? deal : prev)))
        .catch(() => {});
    } else {
      setPickerDeal(null);
    }
    setPickerOpen(true);
  };

  useEffect(() => {
    if (openPickerOnLoad && !templatesLoading && context.portalId && context.dealId) {
      setOpenPickerOnLoad(false);
      handleOpenPicker();
    }
  }, [openPickerOnLoad, templatesLoading, context.portalId, context.dealId]);

  /**
   * Zoals een leeg document: één lege pagina waarin je direct kunt typen.
   * Via de API en niet de store: die zou de offerte zonder pagina als
   * `currentQuote` klaarzetten, en dan opent de editor met nul pagina's.
   */
  const createBlankQuote = async (portalId: string, dealId: string) => {
    const quote = await api.createQuote({
      title: 'Nieuwe Offerte',
      status: QuoteStatus.DRAFT,
      portalId,
      dealId,
    });
    const clientLogoUrl = getQuoteClientLogo([], quote.layoutConfig);
    await api.createBlock(quote.id, {
      type: BlockType.CONTENT_PAGE,
      order: 0,
      content: { title: '', content: '<p></p>', style: 'default', ...(clientLogoUrl ? { clientLogoUrl } : {}) },
    });
    return quote;
  };

  /** `choice`: '__blank__' voor een lege offerte, anders een template-id. */
  const handleCreateQuote = async (choice: string, dealId: string) => {
    if (!context.portalId) {
      toast.error('Portal ID ontbreekt.');
      return;
    }
    try {
      const newQuote =
        choice === '__blank__'
          ? await createBlankQuote(context.portalId, dealId)
          : await api.createQuoteFromTemplate({
              portalId: context.portalId,
              dealId,
              templateId: choice,
              title: 'Nieuwe Offerte',
            });
      navigate(`/quotes/${newQuote.id}/edit`);
    } catch (error) {
      console.error('Failed to create quote:', error);
      toast.error('Offerte aanmaken mislukt', { error });
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!saveAsTemplateName.trim() || !saveAsTemplateQuoteId) return;
    setSaveAsTemplateLoading(true);
    try {
      await api.createTemplateFromQuote({
        quoteId: saveAsTemplateQuoteId,
        name: saveAsTemplateName.trim(),
        description: saveAsTemplateDesc.trim() || undefined,
      });
      toast.success('Template opgeslagen!');
      setSaveAsTemplateOpen(false);
      setSaveAsTemplateName('');
      setSaveAsTemplateDesc('');
      setSaveAsTemplateQuoteId('');
      await refreshTemplates();
    } catch (err) {
      console.error('Save as template failed:', err);
      toast.error('Template opslaan mislukt', { error: err });
    } finally {
      setSaveAsTemplateLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await api.deleteTemplate(deleteTarget.id);
      await refreshTemplates();
      toast.success('Template verwijderd');
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Verwijderen mislukt', { error: err });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleQuoteResendEmail = useCallback(async (quoteId: string, type: 'countersign' | 'signed') => {
    const key = `${quoteId}:${type}`;
    setEmailResendBusy(key);
    try {
      const result = await api.resendQuoteNotification(quoteId, type);
      if (result.sent) {
        toast.success(
          type === 'countersign'
            ? 'Medeondertekening-mail verzonden'
            : 'Teamnotificatie verzonden',
        );
      } else {
        toast.warning(
          'E-mail is niet verzonden (SMTP niet geconfigureerd of geen ontvangers).',
        );
      }
    } catch (err: unknown) {
      toast.error('Versturen mislukt', { error: err });
    } finally {
      setEmailResendBusy(null);
    }
  }, []);

  const handleOpenCountersign = useCallback(async (quote: Quote) => {
    if (quote.countersignUrl) {
      window.open(quote.countersignUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const popup = window.open('', '_blank');
    if (popup) {
      popup.opener = null;
      popup.document.title = 'Medeonderteken-link openen';
      popup.document.body.innerHTML = `
        <main style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#f8fafc;color:#334155">
          <section style="max-width:420px;text-align:center;padding:24px">
            <p style="font-size:16px;font-weight:600;margin:0 0 8px">Medeonderteken-link ophalen...</p>
            <p style="font-size:14px;margin:0;color:#64748b">Deze tab opent zo de link voor de toegewezen UBO-tekenaar.</p>
          </section>
        </main>
      `;
    }
    setCountersignLinkBusy(quote.id);
    try {
      const result = await api.getQuoteCountersignLink(quote.id);
      if (!result.countersignUrl) {
        throw new Error('Backend gaf geen medeonderteken-link terug');
      }
      if (popup) {
        popup.location.replace(result.countersignUrl);
      } else {
        window.open(result.countersignUrl, '_blank', 'noopener,noreferrer');
      }
      await fetchVisibleQuotes();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err as { message?: string })?.message ||
        'Medeonderteken-link ophalen mislukt';
      if (popup && !popup.closed) {
        const safeMsg = escapePopupHtml(msg);
        popup.document.title = 'Medeonderteken-link niet beschikbaar';
        popup.document.body.innerHTML = `
          <main style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#fff7ed;color:#7c2d12">
            <section style="max-width:480px;text-align:center;padding:24px">
              <p style="font-size:18px;font-weight:700;margin:0 0 8px">Link niet beschikbaar</p>
              <p style="font-size:14px;line-height:1.5;margin:0;color:#9a3412">${safeMsg}</p>
            </section>
          </main>
        `;
      }
      toast.error('Medeonderteken-link ophalen mislukt', { error: err });
    } finally {
      setCountersignLinkBusy(null);
    }
  }, [fetchVisibleQuotes]);

  const handleDownloadQuotePdf = useCallback(async (quote: Quote) => {
    if (pdfDownloadBusy) return;

    setPdfDownloadBusy(quote.id);
    try {
      await api.downloadPDF(quote.id, `${quote.title || 'offerte'}.pdf`);
      toast.success('PDF gedownload');
    } catch (error) {
      toast.error('PDF downloaden mislukt', { error });
    } finally {
      setPdfDownloadBusy(null);
    }
  }, [pdfDownloadBusy]);

  /**
   * Interne blik op wat de klant ziet. `preview=1` zorgt dat de publieke pagina
   * er geen view voor registreert: anders telt elke controle door sales mee in
   * 'x keer bekeken', en dat cijfer moet een klantsignaal blijven.
   */
  const handleViewQuote = useCallback((quote: Quote) => {
    if (!quote.slug) {
      toast.error('Deze offerte heeft nog geen publieke link');
      return;
    }
    window.open(`/q/${encodeURIComponent(quote.slug)}?preview=1`, '_blank', 'noopener,noreferrer');
  }, []);

  const openNewTemplate = () => {
    setNewTemplateName('');
    setNewTemplateDesc('');
    setNewTemplateSource('__blank__');
    setNewTemplateOpen(true);
  };

  /** Een nieuwe template opent meteen in de editor: leeg (één lege pagina) of als kopie. */
  const handleCreateTemplate = async () => {
    const name = newTemplateName.trim();
    if (!name) return;
    setNewTemplateLoading(true);
    try {
      const description = newTemplateDesc.trim() || undefined;
      const template =
        newTemplateSource === '__blank__'
          ? await api.createTemplate({
              name,
              description,
              blocks: [
                {
                  type: BlockType.CONTENT_PAGE,
                  order: 0,
                  content: { title: '', content: '<p></p>', style: 'default' },
                },
              ],
            })
          : await api.duplicateTemplate(newTemplateSource, { name, description });
      setNewTemplateOpen(false);
      navigate(`/templates/${template.id}/edit`);
    } catch (err) {
      console.error('Create template failed:', err);
      toast.error('Template aanmaken mislukt', { error: err });
    } finally {
      setNewTemplateLoading(false);
    }
  };

  const companyNames = useMemo(() => {
    const names = new Set<string>();
    for (const q of quotes) {
      if (q.companyName) names.add(q.companyName);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'nl'));
  }, [quotes]);

  const ownerOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const q of quotes) {
      if (q.dealOwnerId) {
        map.set(q.dealOwnerId, getQuoteOwnerLabel(q) || 'Onbekend');
      }
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'nl'));
  }, [quotes]);

  /** Alles behalve Gearchiveerd: tellers, signalen en de pipeline gaan alleen over lopende offertes. */
  const activeQuotes = useMemo(() => quotes.filter((q) => !q.archivedAt), [quotes]);

  const quotesByStatusTab = useMemo(() => {
    const counts: Record<QuoteStatusTab, number> = {
      alles: 0, concept: 0, openstaand: 0, geaccepteerd: 0, afgewezen: 0, spook: 0, gearchiveerd: 0,
    };
    for (const q of quotes) {
      for (const tab of Object.keys(STATUS_TAB_LABELS) as QuoteStatusTab[]) {
        if (matchesStatusTab(q, tab, hideGhostQuotes)) counts[tab]++;
      }
    }
    return counts;
  }, [quotes, hideGhostQuotes]);

  /**
   * Zoeken, filteren en sorteren zonder statustab. De pipelineweergave gebruikt
   * deze lijst rechtstreeks: daar zijn de statussen juist de kolommen.
   */
  const searchedQuotes = useMemo(() => {
    let result = [...quotes];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((quote) =>
        quote.title.toLowerCase().includes(q) ||
        (quote.dealName && quote.dealName.toLowerCase().includes(q)) ||
        (quote.dealId && quote.dealId.toLowerCase().includes(q)) ||
        (quote.companyName && quote.companyName.toLowerCase().includes(q)) ||
        getQuoteOwnerSearchText(quote).includes(q)
      );
    }

    if (companyFilter !== 'all') {
      result = result.filter((quote) => quote.companyName === companyFilter);
    }

    if (ownerFilter !== 'all') {
      result = result.filter((quote) => quote.dealOwnerId === ownerFilter);
    }

    if (dateFilter !== 'all') {
      result = result.filter((quote) => {
        const d = new Date(quote.updatedAt);
        switch (dateFilter) {
          case 'today': return isWithinDays(d, 1);
          case 'week': return isWithinDays(d, 7);
          case 'month': return isWithinDays(d, 30);
          case 'older': return !isWithinDays(d, 30);
          default: return true;
        }
      });
    }

    result = result.filter((quote) => matchesAdvancedFilters(quote, advancedFilters));

    result.sort((a, b) => {
      switch (sortBy) {
        case 'updated_desc': return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        case 'created_desc': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'created_asc': return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'name_asc': return a.title.localeCompare(b.title, 'nl');
        case 'name_desc': return b.title.localeCompare(a.title, 'nl');
        default: return 0;
      }
    });

    return result;
  }, [quotes, searchQuery, companyFilter, ownerFilter, dateFilter, sortBy, advancedFilters]);

  const searchedActiveQuotes = useMemo(() => searchedQuotes.filter((q) => !q.archivedAt), [searchedQuotes]);

  /** Tellers van 'Vraagt actie': over alle statussen heen, binnen de huidige zoekopdracht. */
  const signalCounts = useMemo(() => countSignals(searchedActiveQuotes), [searchedActiveQuotes]);

  const filteredQuotes = useMemo(
    () =>
      signalFilter
        ? searchedActiveQuotes.filter((q) => matchesSignal(q, signalFilter))
        : searchedQuotes.filter((q) => matchesStatusTab(q, quoteStatusTab, hideGhostQuotes)),
    [searchedQuotes, searchedActiveQuotes, signalFilter, quoteStatusTab, hideGhostQuotes],
  );
  const viewingArchive = quoteStatusTab === 'gearchiveerd' && !signalFilter;

  const filteredValueCents = useMemo(
    () => filteredQuotes.reduce((sum, quote) => sum + (quote.valueCents ?? 0), 0),
    [filteredQuotes],
  );
  const filteredValueMaxCents = useMemo(() => quotesMaxValueCents(filteredQuotes), [filteredQuotes]);

  const allFilteredSelected = filteredQuotes.length > 0 && filteredQuotes.every((q) => selectedQuoteIds.has(q.id));
  const someFilteredSelected = filteredQuotes.some((q) => selectedQuoteIds.has(q.id));

  const toggleSelectAll = useCallback(() => {
    if (allFilteredSelected) {
      setSelectedQuoteIds(new Set());
    } else {
      setSelectedQuoteIds(new Set(filteredQuotes.map((q) => q.id)));
    }
  }, [allFilteredSelected, filteredQuotes]);

  const filteredTemplates = useMemo(() => {
    let result = [...templates];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((t) => t.name.toLowerCase().includes(q));
    }
    result.sort((a, b) => {
      switch (templateSortBy) {
        case 'created_desc': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'created_asc': return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'name_asc': return a.name.localeCompare(b.name, 'nl');
        case 'name_desc': return b.name.localeCompare(a.name, 'nl');
        default: return 0;
      }
    });
    return result;
  }, [templates, searchQuery, templateSortBy]);

  /** Aantal spookoffertes dat de Concept-tab standaard weglaat. */
  const ghostQuoteCount = useMemo(
    () => activeQuotes.filter((q) => q.isGhost && STATUS_TAB_FILTERS.concept.includes(q.status)).length,
    [activeQuotes],
  );

  const advancedFilterChips = useMemo(() => describeAdvancedFilters(advancedFilters), [advancedFilters]);
  const activeFiltersCount =
    [companyFilter !== 'all', ownerFilter !== 'all', dateFilter !== 'all'].filter(Boolean).length +
    advancedFilterChips.length +
    (signalFilter ? 1 : 0);

  const resetQuoteFilters = useCallback(() => {
    setSearchQuery('');
    setCompanyFilter('all');
    setOwnerFilter('all');
    setDateFilter('all');
    setAdvancedFilters(DEFAULT_ADVANCED_FILTERS);
    setSignalFilter(null);
  }, []);

  const isQuotesTab = activeTab === 'quotes';
  /**
   * De pipeline is een eigen tab, geen weergave van de lijst: hij toont alle
   * statussen tegelijk en heeft dus geen statustabs, bulkselectie of
   * kaart/lijst-schakelaar. Zoeken en filteren deelt hij wel met het overzicht.
   */
  const isPipelineTab = activeTab === 'pipeline';
  const isQuoteDataTab = isQuotesTab || isPipelineTab;
  const totalCount = isPipelineTab
    ? activeQuotes.length
    : isQuotesTab
      ? quotesByStatusTab[quoteStatusTab]
      : templates.length;
  const filteredCount = isPipelineTab
    ? searchedActiveQuotes.length
    : isQuotesTab
      ? filteredQuotes.length
      : filteredTemplates.length;
  const hasActiveSearch = Boolean(searchQuery.trim()) || activeFiltersCount > 0;

  /**
   * Eén set handlers per offerte, gedeeld door de lijst- en de kaartweergave.
   * Zo kan een actie niet in de ene weergave iets anders doen dan in de andere.
   */
  const quoteActionsFor = useCallback(
    (quote: Quote): QuoteActionHandlers => ({
      onEdit: () => navigate(`/quotes/${quote.id}/edit`),
      onMcpConnect: () => navigate('/mcp'),
      onView: () => handleViewQuote(quote),
      onSaveAsTemplate: () => {
        setSaveAsTemplateQuoteId(quote.id);
        setSaveAsTemplateName(quote.title);
        setSaveAsTemplateDesc('');
        setSaveAsTemplateOpen(true);
      },
      onDuplicate: async () => {
        try {
          await api.duplicateQuote(quote.id, {
            dealId: context.dealId,
            portalId: context.portalId,
          });
          await fetchVisibleQuotes();
          toast.success('Offerte gedupliceerd');
        } catch (error) {
          toast.error('Dupliceren mislukt', { error });
        }
      },
      onDownloadPdf: () => void handleDownloadQuotePdf(quote),
      onHours: () => navigate(`/quotes/${quote.id}/hours`),
      onArchive: () => void changeArchived([quote.id], true),
      onRestore: () => void changeArchived([quote.id], false),
      onExtend: () => openExtendDialog(quote.id),
      onWithdraw: () => setWithdrawTarget(quote),
      onResendEmail: (type) => void handleQuoteResendEmail(quote.id, type),
      onOpenCountersign: () => void handleOpenCountersign(quote),
    }),
    [
      navigate,
      context.dealId,
      context.portalId,
      handleViewQuote,
      fetchVisibleQuotes,
      handleDownloadQuotePdf,
      changeArchived,
      openExtendDialog,
      handleQuoteResendEmail,
      handleOpenCountersign,
    ],
  );

  const quoteBusyState = useMemo(
    () => ({ emailResendBusy, countersignLinkBusy, pdfDownloadBusy }),
    [emailResendBusy, countersignLinkBusy, pdfDownloadBusy],
  );

  const sortColumn: 'updated' | 'created' | 'title' | null =
    sortBy === 'updated_desc'
      ? 'updated'
      : sortBy === 'created_desc' || sortBy === 'created_asc'
        ? 'created'
        : 'title';

  return (
    <PageContainer>
      {/*
        Kop: de titel zegt waar je bent, de regel eronder waar je naar kijkt.
        Die tweede regel verving de aparte 'scope'-kaart die eerder een volle
        rij innam om hetzelfde te zeggen.
      */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold tracking-tight">Offertes</h1>
          <p className="text-sm text-muted-foreground">
            {[
              quoteScope === 'portal' ? 'Salesoverzicht van alle deals' : 'Offertes van deze deal',
              activeQuotes.length > 0 ? `${activeQuotes.length} offertes` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/products')}>
            <Package className="mr-2 h-4 w-4" />
            Producten
          </Button>
          <Button onClick={() => handleOpenPicker()} disabled={needsHubSpotSetup}>
            <Plus className="mr-2 h-4 w-4" />
            Nieuwe offerte
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Meer acties">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate('/mcp')}>
                <Plug className="h-4 w-4" />
                MCP verbinden
              </DropdownMenuItem>
              {/* In the HubSpot iframe the card owns the session; logging out only makes sense in a tab. */}
              {window.self === window.top && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() =>
                      logoutHubSpotSession().catch((error) =>
                        toast.error('Uitloggen is niet gelukt. Probeer het opnieuw.', { error })
                      )
                    }
                  >
                    <LogOut className="h-4 w-4" />
                    Uitloggen
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {needsHubSpotSetup && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-4">
          <p className="text-sm font-semibold text-amber-950">
            {!isAuthenticated
              ? 'HubSpot-sessie ontbreekt'
              : 'Portal-ID ontbreekt voor dit overzicht'}
          </p>
          <p className="mt-1 text-sm text-amber-900">
            {!isAuthenticated
              ? 'Open het overzicht opnieuw via HubSpot, of verbind hieronder. Zonder sessie kunnen we geen offertes laden.'
              : 'Open dit overzicht via de HubSpot-card of gebruik een link met ?portalId=... . Zonder portal-ID blijft de lijst leeg.'}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => void handleConnectHubSpot()} disabled={connectingHubSpot}>
              {connectingHubSpot ? 'Bezig met verbinden…' : 'HubSpot verbinden'}
            </Button>
            {!missingPortalId && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => void fetchVisibleQuotes()}
              >
                Opnieuw laden
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); clearSelection(); }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <TabsList>
              <TabsTrigger value="quotes">
                Offertes
                {activeQuotes.length > 0 && (
                  <span className="ml-1.5 text-xs text-muted-foreground">({activeQuotes.length})</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="pipeline" title="Alle offertes per fase, met bedrag en aantal per kolom">
                Pipeline
              </TabsTrigger>
              <TabsTrigger value="templates">
                Templates
                {templates.length > 0 && (
                  <span className="ml-1.5 text-xs text-muted-foreground">({templates.length})</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="pages"
                title="Losse pagina’s die je in elke offerte invoegt via Pagina toevoegen"
              >
                Pagina’s
                {libraryPageCount > 0 && (
                  <span className="ml-1.5 text-xs text-muted-foreground">({libraryPageCount})</span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Bereik hoort bij de weergave, dus staat het naast de tabs in plaats van in een eigen kaart. */}
          {isQuoteDataTab && showQuoteScopeSwitch && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                Bereik
              </span>
              <div className="flex rounded-md border bg-muted/40 p-1">
                <Button
                  type="button"
                  size="sm"
                  variant={quoteScope === 'deal' ? 'default' : 'ghost'}
                  className="h-7"
                  onClick={() => handleQuoteScopeChange('deal')}
                  title="Alleen offertes van de huidige HubSpot deal"
                >
                  Deze deal
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={quoteScope === 'portal' ? 'default' : 'ghost'}
                  className="h-7"
                  onClick={() => handleQuoteScopeChange('portal')}
                  title="Alle offertes binnen deze portal"
                >
                  Salesoverzicht
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'templates' && (
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={openNewTemplate} disabled={needsHubSpotSetup}>
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Nieuwe template
              </Button>
              <div className="flex items-center gap-2">
                <Switch id="archived" checked={includeArchived} onCheckedChange={setIncludeArchived} />
                <Label htmlFor="archived" className="text-sm cursor-pointer">Gearchiveerd</Label>
              </div>
            </div>
          )}

          {activeTab === 'pages' && (
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setNewLibraryPageOpen(true)}
                disabled={needsHubSpotSetup}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Nieuwe pagina
              </Button>
              <div className="flex items-center gap-2">
                <Switch id="archived-pages" checked={includeArchived} onCheckedChange={setIncludeArchived} />
                <Label htmlFor="archived-pages" className="text-sm cursor-pointer">Gearchiveerd</Label>
              </div>
            </div>
          )}
        </div>

        {/* Status sub-tabs for quotes */}
        {isQuotesTab && (
          <div className="flex items-center gap-1 mb-4 border-b">
            {(Object.keys(STATUS_TAB_LABELS) as QuoteStatusTab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => { setQuoteStatusTab(tab); setSignalFilter(null); clearSelection(); }}
                title={tab === 'spook' ? GHOST_TAB_HINT : tab === 'gearchiveerd' ? ARCHIVE_TAB_HINT : undefined}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === 'gearchiveerd' ? 'ml-auto' : ''
                } ${
                  quoteStatusTab === tab && !signalFilter
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
                }`}
              >
                {tab === 'spook' && <Ghost className="mr-1.5 h-3.5 w-3.5 shrink-0" />}
                {tab === 'gearchiveerd' && <Archive className="mr-1.5 h-3.5 w-3.5 shrink-0" />}
                {STATUS_TAB_LABELS[tab]}
                {/* Het archief groeit alleen maar; een teller daar zegt niets. */}
                {tab !== 'gearchiveerd' && (
                  <span className={`ml-1.5 text-xs ${
                    quoteStatusTab === tab && !signalFilter ? 'text-primary' : 'text-muted-foreground'
                  }`}>
                    ({quotesByStatusTab[tab]})
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Vraagt actie: werkvoorraad die dwars door de statussen loopt. */}
        {isQuotesTab && (
          <div className="mb-4">
            <QuoteSignalFilters
              counts={signalCounts}
              active={signalFilter}
              onChange={(signal) => { setSignalFilter(signal); clearSelection(); }}
            />
          </div>
        )}

        {/*
          Filterbalk: zoeken krijgt de ruimte, de drie filters die je dagelijks
          gebruikt staan open, de rest zit achter 'Meer filters'. Rechts staat
          wat de weergave bepaalt (sorteren, kaart/lijst, selecteren).
        */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[240px] flex-1">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={
                isQuoteDataTab
                  ? 'Zoek op offerte, deal of bedrijf...'
                  : activeTab === 'pages'
                    ? 'Zoek op naam, categorie of beschrijving...'
                    : 'Zoek op templatenaam...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 pl-9"
            />
          </div>

          {isQuoteDataTab && (
            <>
              {companyNames.length > 0 && (
                <Select value={companyFilter} onValueChange={setCompanyFilter}>
                  <SelectTrigger className="h-9 w-[180px]">
                    <Building2 className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                    <SelectValue placeholder="Bedrijf" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle bedrijven</SelectItem>
                    {companyNames.map((name) => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {ownerOptions.length > 0 && (
                <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                  <SelectTrigger className="h-9 w-[180px]">
                    <User className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                    <SelectValue placeholder="Eigenaar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle eigenaren</SelectItem>
                    {ownerOptions.map(({ id, name }) => (
                      <SelectItem key={id} value={id}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="h-9 w-[140px]">
                  <SelectValue placeholder="Datum" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle data</SelectItem>
                  <SelectItem value="today">Vandaag</SelectItem>
                  <SelectItem value="week">Deze week</SelectItem>
                  <SelectItem value="month">Deze maand</SelectItem>
                  <SelectItem value="older">Ouder</SelectItem>
                </SelectContent>
              </Select>

              {isQuotesTab && (
                <QuoteMoreFilters
                  filters={advancedFilters}
                  onChange={(next) => { setAdvancedFilters(next); clearSelection(); }}
                  resultCount={filteredQuotes.length}
                  ghostCount={ghostQuoteCount}
                />
              )}
            </>
          )}

          {isQuotesTab && <div className="mx-1 hidden h-6 w-px bg-border sm:block" />}

          <Select
            value={isQuoteDataTab ? sortBy : templateSortBy}
            onValueChange={(v) =>
              isQuoteDataTab ? setSortBy(v as SortOption) : setTemplateSortBy(v as TemplateSortOption)
            }
          >
            <SelectTrigger className="h-9 w-[180px]">
              <ArrowUpDown className="mr-1.5 h-3.5 w-3.5 shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(isQuoteDataTab ? (Object.keys(SORT_LABELS) as SortOption[]) : TEMPLATE_SORT_OPTIONS).map((key) => (
                <SelectItem key={key} value={key}>{SORT_LABELS[key]}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {isQuotesTab && (
            <>
              <div className="flex items-center gap-0.5 rounded-md bg-muted p-0.5">
                <Button
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="sm"
                  className={cn('h-8 px-2.5', viewMode === 'grid' && 'bg-card shadow-sm hover:bg-card')}
                  onClick={() => handleViewModeChange('grid')}
                  aria-label="Kaartweergave"
                  aria-pressed={viewMode === 'grid'}
                  title="Kaartweergave"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="sm"
                  className={cn('h-8 px-2.5', viewMode === 'list' && 'bg-card shadow-sm hover:bg-card')}
                  onClick={() => handleViewModeChange('list')}
                  aria-label="Lijstweergave"
                  aria-pressed={viewMode === 'list'}
                  title="Lijstweergave"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>

              <Button
                variant={selectionMode ? 'secondary' : 'outline'}
                size="sm"
                className="h-9"
                onClick={() => {
                  if (selectionMode) clearSelection();
                  else setSelectionMode(true);
                }}
              >
                <CheckSquare className="h-4 w-4 mr-1.5" />
                Selecteren
              </Button>
            </>
          )}
        </div>

        {/* Bulk action bar */}
        {selectionMode && selectedQuoteIds.size > 0 && (
          <div className="flex items-center gap-3 mb-4 p-3 bg-muted/60 border rounded-lg">
            <Checkbox
              checked={allFilteredSelected ? true : someFilteredSelected ? 'indeterminate' : false}
              onCheckedChange={toggleSelectAll}
            />
            <span className="text-sm font-medium">
              {selectedQuoteIds.size} geselecteerd
            </span>
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleBulkArchive(!viewingArchive)}
              disabled={bulkArchiveLoading}
            >
              {viewingArchive ? (
                <ArchiveRestore className="h-3.5 w-3.5 mr-1.5" />
              ) : (
                <Archive className="h-3.5 w-3.5 mr-1.5" />
              )}
              {viewingArchive ? 'Terugzetten' : 'Archiveren'} ({selectedQuoteIds.size})
            </Button>
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Resultaatregel: hoeveel er overblijft, en welke filters dat veroorzaken. */}
        {hasActiveSearch && activeTab !== 'pages' && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">
              {filteredCount} van {totalCount} {isQuoteDataTab ? 'offertes' : 'templates'}
            </span>
            <span className="hidden h-4 w-px bg-border sm:block" />
            {signalFilter && (
              <Badge variant="secondary" className="cursor-pointer gap-1" onClick={() => setSignalFilter(null)}>
                {SIGNAL_LABELS[signalFilter]}
                <span className="ml-0.5 text-xs">&times;</span>
              </Badge>
            )}
            {companyFilter !== 'all' && (
              <Badge variant="secondary" className="cursor-pointer gap-1" onClick={() => setCompanyFilter('all')}>
                {companyFilter}
                <span className="ml-0.5 text-xs">&times;</span>
              </Badge>
            )}
            {ownerFilter !== 'all' && (
              <Badge variant="secondary" className="cursor-pointer gap-1" onClick={() => setOwnerFilter('all')}>
                {ownerOptions.find((o) => o.id === ownerFilter)?.name || 'Dealeigenaar'}
                <span className="ml-0.5 text-xs">&times;</span>
              </Badge>
            )}
            {dateFilter !== 'all' && (
              <Badge variant="secondary" className="cursor-pointer gap-1" onClick={() => setDateFilter('all')}>
                {dateFilter === 'today' ? 'Vandaag' : dateFilter === 'week' ? 'Deze week' : dateFilter === 'month' ? 'Deze maand' : 'Ouder'}
                <span className="ml-0.5 text-xs">&times;</span>
              </Badge>
            )}
            {advancedFilterChips.map((chip) => (
              <Badge
                key={`${chip.key}-${chip.label}`}
                variant="secondary"
                className="cursor-pointer gap-1"
                onClick={() => setAdvancedFilters((prev) => clearAdvancedFilter(prev, chip.key))}
              >
                {chip.label}
                <span className="ml-0.5 text-xs">&times;</span>
              </Badge>
            ))}
            <Button variant="ghost" size="sm" className="ml-auto h-6 text-xs" onClick={resetQuoteFilters}>
              Wis filters
            </Button>
          </div>
        )}

        {/* Quotes Tab */}
        <TabsContent value="quotes">
          {quotesLoading ? (
            <SkeletonCards />
          ) : quotes.length === 0 ? (
            <div className="py-16 text-center">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
              <h2 className="text-xl font-semibold text-muted-foreground mb-2">
                {needsHubSpotSetup ? 'Overzicht nog niet beschikbaar' : 'Nog geen offertes'}
              </h2>
              <p className="text-muted-foreground mb-6">
                {needsHubSpotSetup
                  ? 'Verbind eerst met HubSpot of open het overzicht via de HubSpot-card, zodat portal-ID en sessie meegaan.'
                  : quoteScope === 'portal'
                    ? 'Er zijn nog geen offertes binnen deze portal.'
                    : 'Maak je eerste offerte aan om te beginnen'}
              </p>
              <Button size="lg" onClick={() => handleOpenPicker()}>
                <Plus className="mr-2 h-4 w-4" />
                Nieuwe Offerte
              </Button>
            </div>
          ) : filteredQuotes.length === 0 && viewingArchive && !hasActiveSearch ? (
            <div className="rounded-md border border-dashed bg-card py-16 text-center">
              <Archive className="mx-auto mb-4 h-10 w-10 text-muted-foreground/50" />
              <h2 className="mb-2 text-lg font-semibold text-muted-foreground">Geen gearchiveerde offertes</h2>
              <p className="mx-auto max-w-md text-sm text-muted-foreground">{ARCHIVE_TAB_HINT}</p>
            </div>
          ) : filteredQuotes.length === 0 && quoteStatusTab === 'spook' && !signalFilter && !hasActiveSearch ? (
            <div className="rounded-md border border-dashed bg-card py-16 text-center">
              <Ghost className="mx-auto mb-4 h-10 w-10 text-muted-foreground/50" />
              <h2 className="mb-2 text-lg font-semibold text-muted-foreground">Geen spookoffertes</h2>
              <p className="mx-auto max-w-md text-sm text-muted-foreground">{GHOST_TAB_HINT}</p>
            </div>
          ) : filteredQuotes.length === 0 ? (
            <div className="rounded-md border border-dashed bg-card px-6 py-16 text-center">
              <Search className="mx-auto mb-4 h-10 w-10 text-muted-foreground/50" />
              <h2 className="mb-2 text-base font-semibold">Geen offertes gevonden</h2>
              <p className="mx-auto mb-5 max-w-md text-sm text-muted-foreground">
                {signalFilter
                  ? `Geen offertes met het signaal ${SIGNAL_LABELS[signalFilter]} binnen de huidige zoekopdracht.`
                  : `Geen resultaten in ${STATUS_TAB_LABELS[quoteStatusTab]}${
                      activeFiltersCount > 0 ? ` met ${activeFiltersCount} actieve filters` : ''
                    }.`}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button variant="outline" onClick={resetQuoteFilters}>
                  Wis filters
                </Button>
                {signalFilter && (
                  <Button onClick={() => setSignalFilter(null)}>Terug naar {STATUS_TAB_LABELS[quoteStatusTab]}</Button>
                )}
              </div>
            </div>
          ) : viewMode === 'list' ? (
            <QuoteTable
              quotes={filteredQuotes}
              totalCount={totalCount}
              portalId={context.portalId}
              selectionMode={selectionMode}
              selectedIds={selectedQuoteIds}
              onToggleSelect={toggleQuoteSelection}
              onToggleSelectAll={toggleSelectAll}
              allSelected={allFilteredSelected}
              someSelected={someFilteredSelected}
              actionsFor={quoteActionsFor}
              busy={quoteBusyState}
              sortColumn={sortColumn}
            />
          ) : (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredQuotes.map((quote) => (
                  <QuoteCard
                    key={quote.id}
                    quote={quote}
                    portalId={context.portalId}
                    selected={selectedQuoteIds.has(quote.id)}
                    selectionMode={selectionMode}
                    onToggleSelect={() => toggleQuoteSelection(quote.id)}
                    actions={quoteActionsFor(quote)}
                    busy={quoteBusyState}
                  />
                ))}
              </div>
              <QuoteTotalsFooter
                shownCount={filteredQuotes.length}
                totalCount={totalCount}
                totalCents={filteredValueCents}
                totalMaxCents={filteredValueMaxCents}
                className="border-t px-1 pb-0"
              />
            </div>
          )}
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline">
          {quotesLoading ? (
            <SkeletonCards />
          ) : activeQuotes.length === 0 ? (
            <div className="py-16 text-center">
              <Columns3 className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
              <h2 className="text-xl font-semibold text-muted-foreground mb-2">
                {needsHubSpotSetup ? 'Pipeline nog niet beschikbaar' : 'Nog geen offertes'}
              </h2>
              <p className="text-muted-foreground">
                {needsHubSpotSetup
                  ? 'Verbind eerst met HubSpot of open het overzicht via de HubSpot-card, zodat portal-ID en sessie meegaan.'
                  : 'Zodra er offertes zijn, verschijnen ze hier per fase met bedrag en aantal.'}
              </p>
            </div>
          ) : searchedActiveQuotes.length === 0 ? (
            <div className="py-16 text-center">
              <Search className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
              <h2 className="text-lg font-semibold text-muted-foreground mb-2">Geen offertes gevonden</h2>
              <p className="text-sm text-muted-foreground mb-4">Pas je zoekopdracht of filters aan</p>
              <Button variant="outline" onClick={() => { setSearchQuery(''); setCompanyFilter('all'); setOwnerFilter('all'); setDateFilter('all'); }}>
                Wis filters
              </Button>
            </div>
          ) : (
            <QuotePipelineBoard
              quotes={searchedActiveQuotes}
              onOpenQuote={(quote) => navigate(`/quotes/${quote.id}/edit`)}
            />
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates">
          {templatesLoading ? (
            <SkeletonCards />
          ) : templates.length === 0 ? (
            <div className="py-16 text-center">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
              <h2 className="text-xl font-semibold text-muted-foreground mb-2">Nog geen templates</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Maak een nieuwe template, of sla een offerte op als template
              </p>
              <Button variant="outline" onClick={openNewTemplate} disabled={needsHubSpotSetup}>
                <Plus className="h-4 w-4 mr-2" />
                Nieuwe template
              </Button>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="py-16 text-center">
              <Search className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
              <h2 className="text-lg font-semibold text-muted-foreground mb-2">Geen templates gevonden</h2>
              <p className="text-sm text-muted-foreground mb-4">Pas je zoekopdracht aan</p>
              <Button variant="outline" onClick={() => setSearchQuery('')}>
                Wis zoekopdracht
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onPreview={() => { setPreview(template); setPreviewOpen(true); }}
                  onEdit={() => navigate(`/templates/${template.id}/edit`)}
                  // Altijd via de dialoog: daar zie en kies je de deal, ook als er al een in de context staat.
                  onNewQuote={() => handleOpenPicker(template.id)}
                  onSetMaster={async () => {
                    if (!context.portalId) return;
                    try {
                      await api.setMasterTemplate(template.id, context.portalId);
                      await refreshTemplates();
                      toast.success('Master template ingesteld');
                    } catch (error) {
                      toast.error('Master instellen mislukt', { error });
                    }
                  }}
                  onDuplicate={async () => {
                    try {
                      await api.duplicateTemplate(template.id);
                      await refreshTemplates();
                      toast.success('Template gedupliceerd');
                    } catch (error) {
                      toast.error('Dupliceren mislukt', { error });
                    }
                  }}
                  onArchive={async () => {
                    if (!context.portalId) return;
                    try {
                      if (template.archivedAt) {
                        await api.unarchiveTemplate(template.id, context.portalId);
                        toast.success('Template hersteld');
                      } else {
                        await api.archiveTemplate(template.id, context.portalId);
                        toast.success('Template gearchiveerd');
                      }
                      await refreshTemplates();
                    } catch (error) {
                      toast.error(
                        template.archivedAt ? 'Template herstellen mislukt' : 'Template archiveren mislukt',
                        { error }
                      );
                    }
                  }}
                  onDelete={() => {
                    setDeleteTarget({ id: template.id, name: template.name, hasDraft: Boolean(template.draftQuoteId) });
                    setDeleteConfirmOpen(true);
                  }}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Paginabibliotheek */}
        <TabsContent value="pages">
          <PageLibraryTab
            portalId={context.portalId}
            searchQuery={searchQuery}
            onClearSearch={() => setSearchQuery('')}
            sortBy={templateSortBy}
            includeArchived={includeArchived}
            newPageOpen={newLibraryPageOpen}
            onNewPageOpenChange={setNewLibraryPageOpen}
          />
        </TabsContent>
      </Tabs>

      {/* New Quote Picker Dialog */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nieuwe offerte</DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-1.5">
            <Label>Deal</Label>
            <DealPicker value={pickerDeal} onChange={setPickerDeal} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-quote-start">Begin met</Label>
            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
              <SelectTrigger id="new-quote-start" className="w-full">
                <SelectValue placeholder="Kies een template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__blank__">Lege offerte</SelectItem>
                {startableTemplates.length > 0 && <SelectSeparator />}
                {startableTemplates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}{t.isMaster ? ' (master)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                const t = templates.find((x) => x.id === selectedTemplateId) || null;
                setPreview(t);
                setPreviewOpen(true);
              }}
              disabled={!selectedTemplateId || selectedTemplateId === '__blank__'}
            >
              Preview
            </Button>
            <Button variant="outline" onClick={() => setPickerOpen(false)}>
              Annuleren
            </Button>
            <Button
              onClick={async () => {
                if (!pickerDeal) return;
                setPickerOpen(false);
                await handleCreateQuote(selectedTemplateId, pickerDeal.id);
              }}
              disabled={!pickerDeal}
            >
              Maak offerte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save As Template Dialog */}
      <Dialog open={saveAsTemplateOpen} onOpenChange={(open) => { if (!saveAsTemplateLoading) setSaveAsTemplateOpen(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Opslaan als template</DialogTitle>
            <DialogDescription>
              Sla deze offerte op als herbruikbare template
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="tplName">Template naam</Label>
              <Input
                id="tplName"
                value={saveAsTemplateName}
                onChange={(e) => setSaveAsTemplateName(e.target.value)}
                disabled={saveAsTemplateLoading}
                placeholder="Bijv. Standaard offerte"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tplDesc">Beschrijving (optioneel)</Label>
              <textarea
                id="tplDesc"
                value={saveAsTemplateDesc}
                onChange={(e) => setSaveAsTemplateDesc(e.target.value)}
                rows={2}
                disabled={saveAsTemplateLoading}
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Korte beschrijving van deze template..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveAsTemplateOpen(false)} disabled={saveAsTemplateLoading}>
              Annuleren
            </Button>
            <Button onClick={handleSaveAsTemplate} disabled={!saveAsTemplateName.trim() || saveAsTemplateLoading}>
              {saveAsTemplateLoading ? 'Opslaan...' : 'Opslaan als template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={(open) => { if (!deleteLoading) setDeleteConfirmOpen(open); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Verwijderen bevestigen</DialogTitle>
            <DialogDescription>
              Weet je zeker dat je &ldquo;{deleteTarget?.name}&rdquo; wilt verwijderen?
              {deleteTarget?.hasDraft && ' Ook het concept met niet-opgeslagen wijzigingen gaat weg.'} Dit kan niet
              ongedaan worden gemaakt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading}>
              Annuleren
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteLoading}>
              {deleteLoading ? 'Verwijderen...' : 'Verwijderen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Template Dialog */}
      <Dialog open={newTemplateOpen} onOpenChange={(open) => { if (!newTemplateLoading) setNewTemplateOpen(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nieuwe template</DialogTitle>
            <DialogDescription>De template opent in de editor, zodat je hem meteen kunt vullen.</DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void handleCreateTemplate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="new-template-name">Naam</Label>
              <Input
                id="new-template-name"
                autoFocus
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                disabled={newTemplateLoading}
                placeholder="Bijv. Website-offerte"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-template-source">Begin met</Label>
              <Select value={newTemplateSource} onValueChange={setNewTemplateSource} disabled={newTemplateLoading}>
                <SelectTrigger id="new-template-source" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__blank__">Lege template</SelectItem>
                  {startableTemplates.length > 0 && <SelectSeparator />}
                  {startableTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      Kopie van {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-template-desc">Beschrijving (optioneel)</Label>
              <textarea
                id="new-template-desc"
                value={newTemplateDesc}
                onChange={(e) => setNewTemplateDesc(e.target.value)}
                rows={2}
                disabled={newTemplateLoading}
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Waar gebruik je deze template voor?"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewTemplateOpen(false)} disabled={newTemplateLoading}>
                Annuleren
              </Button>
              <Button type="submit" disabled={!newTemplateName.trim() || newTemplateLoading}>
                {newTemplateLoading ? 'Aanmaken…' : 'Aanmaken en bewerken'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <TemplatePreviewDialog
        open={previewOpen}
        template={preview}
        onClose={() => { setPreviewOpen(false); setPreview(null); }}
      />

      {/* Extend Quote Dialog */}
      <Dialog open={extendOpen} onOpenChange={(open) => { if (!extendLoading) setExtendOpen(open); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Offerte verlengen</DialogTitle>
            <DialogDescription>
              Kies een nieuwe verloopdatum. De offerte wordt weer gepubliceerd.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="extendDate">Nieuwe verloopdatum</Label>
              <Input
                id="extendDate"
                type="date"
                value={extendDate}
                onChange={(e) => setExtendDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                disabled={extendLoading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendOpen(false)} disabled={extendLoading}>
              Annuleren
            </Button>
            <Button onClick={handleExtendQuote} disabled={!extendDate || extendLoading}>
              {extendLoading ? 'Verlengen...' : 'Verlengen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw Quote Dialog */}
      <Dialog open={Boolean(withdrawTarget)} onOpenChange={(open) => { if (!withdrawLoading && !open) setWithdrawTarget(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Offerte intrekken?</DialogTitle>
            <DialogDescription>
              De klantlink voor &ldquo;{withdrawTarget?.title}&rdquo; wordt direct onbeschikbaar.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
            De offerte blijft bewaard en verschijnt daarna bij Concept als ingetrokken offerte.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawTarget(null)} disabled={withdrawLoading}>
              Annuleren
            </Button>
            <Button variant="destructive" onClick={handleWithdrawQuote} disabled={withdrawLoading}>
              {withdrawLoading ? 'Intrekken...' : 'Offerte intrekken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </PageContainer>
  );
}

function TemplateCard({
  template,
  onPreview,
  onEdit,
  onNewQuote,
  onSetMaster,
  onDuplicate,
  onArchive,
  onDelete,
}: {
  template: Template;
  onPreview: () => void;
  onEdit: () => void;
  onNewQuote: () => void;
  onSetMaster: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  /**
   * Een lege template levert een lege offerte op; de kaart zegt dat en biedt verwijderen aan.
   * Leeg gaat over wat opgeslagen is; een open concept noemt de kaart apart, en de
   * verwijderbevestiging waarschuwt dat het concept meegaat.
   */
  const isEmpty = isTemplateEmpty(template);
  const pageCount = template.blocks.length;

  return (
    <Card className={cn('group flex flex-col transition-shadow hover:shadow-md', isEmpty && 'border-dashed')}>
      {template.thumbnail && (
        <img src={template.thumbnail} alt={template.name} className="w-full h-40 object-cover rounded-t-lg" />
      )}
      <CardContent className="flex-1 pt-6">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-base font-semibold leading-tight line-clamp-2">{template.name}</h3>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0 text-muted-foreground" aria-label="Meer acties">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="h-4 w-4" />
                Bewerken
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onPreview} disabled={isEmpty}>
                <Eye className="h-4 w-4" />
                Preview
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onNewQuote} disabled={isEmpty || !!template.archivedAt}>
                <Plus className="h-4 w-4" />
                Nieuwe offerte
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onSetMaster} disabled={!!template.archivedAt || isEmpty}>
                <Star className="h-4 w-4" />
                Zet als master
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDuplicate}>
                <Copy className="h-4 w-4" />
                Dupliceren
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onArchive}>
                {template.archivedAt ? <RotateCcw className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                {template.archivedAt ? 'Herstellen' : 'Archiveren'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
                <Trash2 className="h-4 w-4" />
                Verwijderen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {template.isMaster && <Badge variant="default" className="text-xs">Master</Badge>}
          {template.archivedAt && <Badge variant="outline" className="text-xs">Gearchiveerd</Badge>}
          {isEmpty && <Badge variant="outline" className="text-xs text-muted-foreground">Leeg</Badge>}
        </div>
        {isEmpty ? (
          <p className="mt-2 text-sm text-muted-foreground">
            {template.draftQuoteId
              ? 'Er is nog niets in deze template opgeslagen. Rond je concept af of verwijder hem.'
              : 'Deze template heeft nog geen inhoud. Vul hem of verwijder hem.'}
          </p>
        ) : (
          template.description && (
            <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{template.description}</p>
          )
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          {[
            isEmpty ? null : `${pageCount} ${pageCount === 1 ? 'pagina' : "pagina's"}`,
            `Aangemaakt ${new Date(template.createdAt).toLocaleDateString('nl-NL')}${
              template.createdBy ? ` door ${template.createdBy}` : ''
            }`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {template.draftQuoteId && (
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-700">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
            Concept nog niet opgeslagen in de template
          </p>
        )}
      </CardContent>
      <CardFooter className="flex flex-wrap gap-1">
        {isEmpty ? (
          <>
            <Button variant="ghost" size="sm" className="px-2" onClick={onEdit}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Bewerken
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Verwijderen
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" size="sm" className="px-2" onClick={onPreview}>
              <Eye className="mr-1.5 h-3.5 w-3.5" />
              Preview
            </Button>
            <Button variant="ghost" size="sm" className="px-2" onClick={onEdit}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Bewerken
            </Button>
            {!template.archivedAt && (
              <Button variant="ghost" size="sm" className="px-2" onClick={onNewQuote}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Offerte
              </Button>
            )}
          </>
        )}
      </CardFooter>
    </Card>
  );
}

export default HomePage;
